<script lang="ts">
  import { navigate } from '../lib/router.svelte';
  import AdminShell from './AdminShell.svelte';
  import PageContainer from '../lib/ui/PageContainer.svelte';
  import StateBlock from '@ui/StateBlock.svelte';
  import PipelineBoard from '../lib/ui/PipelineBoard.svelte';
  import PipelineCardDrawer from '../lib/ui/PipelineCardDrawer.svelte';
  import PageHeader from '../lib/ui/PageHeader.svelte';
  import RevenueTiles from '../lib/ui/RevenueTiles.svelte';
  import { fetchInsights, type RevenueBlock } from '@api';
  import { setQuery } from '../lib/router.svelte';
  import {
    redirectIfAwaitingOnboarding,
    getToken,
    handle401,
    type AdminUser,
  } from '../lib/admin';
  import { pipelineBlurb, stageOf } from '../lib/pipelines';
  import CardReasonPrompt from '../lib/ui/CardReasonPrompt.svelte';
  import PipelineForm from '../lib/ui/PipelineForm.svelte';
  import MemberPicker from '../lib/ui/MemberPicker.svelte';
  import { onCommunityChange, withCommunity } from '../lib/community.svelte';
  import { asAdminUser, ensureBootstrap } from '../lib/session';
  import { addPipelineCard, archivePipeline, createPipeline, fetchPipelineBoard,
    fetchPipelineStateCards, fetchPipelines, movePipelineCard, removePipelineCard,
    setCardNote, unpinPipelineCard, updatePipeline, type Pipeline, type PipelineBoardData,
    type PipelineCard, type StageDraft } from '@api';

  // Cards fetched per "load more". There is no board-wide sort control: each STATE
  // declares its own ordering server-side, because one sort for every column has to be
  // wrong somewhere — "who is newest" is right where a clock is running, "who has done
  // the most" where the job is pushing someone over a line. Per-column FILTERS are the
  // thing worth exposing here, and are not built yet.
  const PAGE = 50;

  let me = $state<AdminUser | null>(null);
  let loading = $state(true); // cold first load
  let dataLoading = $state(false); // community switch
  let boardLoading = $state(false); // pipeline switch (keeps the switcher visible)
  let error = $state<string | null>(null);
  let boardError = $state<string | null>(null);
  let toast = $state<string | null>(null);
  let toastTimer: ReturnType<typeof setTimeout> | undefined;

  let skoolId = $state<number | null>(null);
  let pipelines = $state<Pipeline[]>([]);

  // The revenue tiles, from the ONE block this page needs off the insights aggregate.
  // Fetched beside the board rather than after it, and deliberately fail-soft: a board
  // that renders is worth more than a revenue tile, so a failure here leaves the tiles
  // absent instead of erroring the page.
  let revenue = $state<RevenueBlock | null>(null);
  let revenueAt = $state<string | null>(null);

  async function loadRevenue(id: number): Promise<void> {
    try {
      const data = await fetchInsights(id, ['revenue'] as const);
      revenue = data.revenue;
      revenueAt = data.last_updated ?? null;
    } catch {
      revenue = null;
      revenueAt = null;
    }
  }
  let pipelineKey = $state<string | null>(null);
  let board = $state<PipelineBoardData | null>(null);
  let loadingMore = $state<Set<string>>(new Set());
  let openCardId = $state<number | null>(null);

  /**
   * THE FILTERS ARRIVE IN THE URL, and every one of them is read from here — one parse,
   * so the three cannot disagree about what the link asked for.
   *
   * A board under a filter is a specific question — "my 12 cancelling members" — and a
   * question you cannot link to is one you rebuild by hand every time. The revenue tiles
   * ANSWER with such a link: every figure opens its own board pre-filtered, which only
   * works if the filter is addressable both ways.
   *
   * Read ONCE at construction: this is the URL the page was opened with. `syncUrl` writes
   * later state back with `replaceState`, so filtering does not stack history entries —
   * the back button leaves the board rather than walking back through every keystroke.
   */
  const _q0 = new URLSearchParams(window.location.search);

  // Board search. TWO variables on purpose: `query` is what the box holds this
  // keystroke, `appliedQuery` is the term the board on screen was actually built from.
  // `onLoadMore` pages against the SERVER's filtered offsets, so it must send the term
  // those offsets belong to — mid-typing, the two differ.
  let query = $state(_q0.get('q') ?? '');
  let appliedQuery = $state(_q0.get('q') ?? '');
  let searchTimer: ReturnType<typeof setTimeout> | undefined;

  // "Only paying" — a board-wide FILTER, in the same family as the search box and applied
  // the same way: it reaches the per-state counts, not just the previews, so a column
  // never heads a filtered list with an unfiltered total. Two variables for the same
  // reason as the search: `onlyPaying` is the checkbox this instant, `appliedPaying` is
  // what the board on screen was actually built from, and paging offsets belong to that.
  //
  // Not debounced — a checkbox is one deliberate click, not a stream of keystrokes.
  let onlyPaying = $state(_q0.get('paying') === '1');
  /** What the board on screen was built from — see `appliedQuery`. */
  let appliedPaying = $state(_q0.get('paying') === '1');

  /** A revenue segment, the one filter with no control of its own — it arrives from a
   *  tile on this page or on the Overview, and the chip below is where it is named. */
  let segment = $state<string | null>(_q0.get('segment'));
  /** What the board ON SCREEN was built from — paging offsets belong to this, not to
   *  whatever the URL says a moment later. Same two-variable rule as `appliedQuery`. */
  let appliedSegment = $state<string | null>(_q0.get('segment'));

  /** Labels for the chip. Keyed by the server's segment vocabulary
   *  (`domain/revenue.py::SEGMENTS`) — the words a person clicked in the revenue row. */
  const SEGMENT_LABEL: Record<string, string> = {
    monthly: 'on monthly plans',
    annual: 'on annual plans',
    unpriced: 'paying, no published price',
    paying: 'paying members',
    collected: 'have ever paid',
    cancelled: 'cancelling',
    dropping_off: 'dropping off',
    risk_to_cancel: 'at risk to cancel',
    engaged: 'paying and engaged',
    churned: 'churned',
  };
  const segmentLabel = $derived(segment ? (SEGMENT_LABEL[segment] ?? segment) : null);
  // `filtered` lived here to decide whether to draw the chip row. Every filter now
  // carries its own visible control, so nothing needs to ask "is anything applied".

  /**
   * THE FILTERS THE BOARD ON SCREEN WAS BUILT FROM — one object, carried by every
   * request that touches this board.
   *
   * Not three loose `applied*` variables read call-site by call-site, because the bug
   * that costs is one this page already shipped: `onSort` re-fetched a column with `q`
   * and `paying` but forgot `segment`, so re-ordering a segment-filtered column silently
   * refilled it with the members the filter had removed — and replaced the column's total
   * with the unfiltered one. A column fetched under a different filter set is a column of
   * a DIFFERENT board: its offsets count rows that aren't there, and its total answers a
   * question nobody asked.
   *
   * Spreads into both fetchers, so adding a fourth filter reaches all three call sites by
   * construction rather than by remembering.
   */
  const appliedFilters = $derived({
    q: appliedQuery,
    paying: appliedPaying,
    // Omitted rather than sent as `undefined`: the fetchers declare it optional under
    // `exactOptionalPropertyTypes`, where a present-but-undefined key is not the same
    // thing as an absent one.
    ...(appliedSegment ? { segment: appliedSegment } : {}),
  });

  function syncUrl(): void {
    setQuery({
      pipeline: pipelineKey,
      segment,
      q: appliedQuery.trim() || null,
      paying: onlyPaying ? '1' : null,
    });
  }

  /**
   * A revenue figure was clicked while we are ALREADY on this page.
   *
   * Navigating to `/admin/pipelines?...` from `/admin/pipelines` changes the URL and
   * nothing else — the component does not remount, and `?segment=` is read on the cold
   * load only, so the figure looked inert. This does the two things the URL would have
   * done on a fresh load: switch to the segment's own board, and apply the filter.
   *
   * `loadBoard` is called directly rather than through `selectPipeline`, which no-ops
   * when the key is unchanged — clicking a figure for the board you are already on must
   * still re-filter it.
   */
  function openSegment(seg: string, board: string): void {
    segment = seg || null;
    if (skoolId == null) return;
    const target = pipelines.some((p) => p.key === board) ? board : (pipelineKey ?? board);
    void loadBoard(skoolId, target);
  }

  /** Drop every filter, without reloading. Separate from `clearFilters` because a
   *  community switch clears the same three and then loads a board of its own — and
   *  because clearing them ONE SHORT is how a stale filter survives into a community it
   *  does not describe. `appliedSegment` included: `loadBoard` overwrites it, but a
   *  caller that clears without loading (a community with no pipelines) would otherwise
   *  leave the chip naming a filter nothing is applying. */
  function resetFilters(): void {
    clearTimeout(searchTimer);
    segment = null;
    appliedSegment = null;
    onlyPaying = false;
    appliedPaying = false;
    query = '';
    appliedQuery = '';
  }

  // `clearFilters` (clear all three, one button) went with the chip row that hosted it.
  // Each filter now clears itself from its own control: the segment pill's ✕, the Only
  // paying checkbox, and the search box's native clear. `resetFilters` below is a
  // different thing and is still used — it drops the filters on a COMMUNITY switch.

  // Per-column sort overrides, `state_key -> sort`. Absent means "the state's own
  // declared ordering", which is what the board already returned — so this map holds
  // only what a reader has actively changed, and an empty map is the designed board.
  let sorts = $state<Record<string, string>>({});

  /** The ordering a column is currently being read in: the reader's override, else the
   *  state's declaration. Every request touching that column has to agree with this. */
  function sortOf(stateKey: string): string | undefined {
    return sorts[stateKey];
  }

  const selected = $derived(pipelines.find((p) => p.key === pipelineKey) ?? null);
  const openCard = $derived<PipelineCard | null>(
    openCardId == null || !board
      ? null
      : (board.stages.flatMap((c) => c.cards).find((c) => c.card_id === openCardId) ?? null),
  );

  let nowMs = $state(Date.now());
  $effect(() => {
    const id = setInterval(() => (nowMs = Date.now()), 30_000);
    return () => clearInterval(id);
  });

  function flash(msg: string): void {
    toast = msg;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => (toast = null), 3200);
  }

  // --- local board mutation (immutable, so a revert is a single reassignment) -------

  function removeCard(b: PipelineBoardData, cardId: number): { board: PipelineBoardData; card: PipelineCard | null } {
    let found: PipelineCard | null = null;
    const stages = b.stages.map((col) => {
      const keep: PipelineCard[] = [];
      for (const c of col.cards) {
        if (c.card_id === cardId) found = c;
        else keep.push(c);
      }
      return { ...col, cards: keep };
    });
    return { board: { ...b, stages }, card: found };
  }

  function placeCard(b: PipelineBoardData, card: PipelineCard): PipelineBoardData {
    const stages = b.stages.map((col) =>
      col.key === card.stage_key ? { ...col, cards: [...col.cards, card] } : col,
    );
    return { ...b, stages };
  }

  // --- manual actions, all of which must be explained -------------------------------
  //
  // Drag-and-drop, the drawer's stage select and the drawer's unpin button do not act:
  // they RAISE a pending action, and the prompt is what performs it. One funnel, so no
  // path can quietly acquire a way to skip the note the server is going to demand.
  //
  // NOTHING IS OPTIMISTIC ANY MORE. A move used to relocate the card on the instant and
  // reconcile after; now there is a modal in the way, so the card would sit in the wrong
  // column for as long as someone takes to type — and land back if they cancelled. The
  // write happens first and the board follows it.
  type Pending =
    | { kind: 'move' | 'pin'; cardId: number; stageKey: string }
    | { kind: 'unpin'; cardId: number };

  let pending = $state<Pending | null>(null);
  let pendingBusy = $state(false);
  let pendingError = $state<string | null>(null);

  /** The card a pending action is about, read off the board so it stays current. */
  const pendingCard = $derived.by<PipelineCard | null>(() => {
    // Read both into locals first: they are mutable `$state`, so a narrowing check does
    // not survive into the closure that follows it.
    const act = pending;
    const b = board;
    if (act == null || b == null) return null;
    return b.stages.flatMap((c) => c.cards).find((c) => c.card_id === act.cardId) ?? null;
  });

  function requestMove(cardId: number, stageKey: string): void {
    if (skoolId == null || pipelineKey == null || !board) return;
    if (stageOf(board.pipeline.stages, stageKey) == null) return;
    const card = board.stages.flatMap((c) => c.cards).find((c) => c.card_id === cardId);
    if (!card) return;
    pendingError = null;
    // Re-asserting the state a card is already in is a real action (it re-pins for 7
    // days) with its own wording, not a no-op to swallow.
    pending = { kind: card.stage_key === stageKey ? 'pin' : 'move', cardId, stageKey };
  }

  function requestUnpin(cardId: number): void {
    pendingError = null;
    pending = { kind: 'unpin', cardId };
  }

  function cancelPending(): void {
    pending = null;
    pendingBusy = false;
    pendingError = null;
  }

  async function confirmPending(reason: string): Promise<void> {
    const act = pending;
    if (act == null || skoolId == null || pipelineKey == null || !board) return;
    pendingBusy = true;
    pendingError = null;
    try {
      const updated =
        act.kind === 'unpin'
          ? await unpinPipelineCard(skoolId, pipelineKey, act.cardId, reason)
          : await movePipelineCard(skoolId, pipelineKey, act.cardId, act.stageKey, reason);
      if (board) board = placeCard(removeCard(board, act.cardId).board, updated);
      pending = null;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      // Shown IN the prompt, not as a toast: the reason they typed is still in the box
      // and the retry is one click away. A toast would dismiss the modal and lose it.
      pendingError = err instanceof Error ? err.message : 'That did not go through.';
    } finally {
      pendingBusy = false;
    }
  }

  // --- authoring a manual board ----------------------------------------------------
  //
  // Only ever offered for `pipeline.manual`. On a computed board every member already
  // holds a permanent card, so "add" has nothing to do and "remove" would be undone by
  // the next tick — the affordances are absent rather than disabled, because a control
  // that cannot work is worse than no control.

  let archived = $state<Pipeline[]>([]);
  let form = $state<{ mode: 'create' | 'edit'; initial: Pipeline | null } | null>(null);
  let formBusy = $state(false);
  let formError = $state<string | null>(null);

  let adding = $state<string | null>(null); // the column `+ Add` was pressed in
  let addBusy = $state(false);
  let addError = $state<string | null>(null);

  const editable = $derived(selected?.manual === true);

  /** Members the board can prove already hold a card here, so the picker can grey them
   *  out instead of offering a click that 409s. A column ships only a PREVIEW, so this is
   *  a courtesy and not a guarantee — `already_on_board` from the server stays the actual
   *  guard, and its message is surfaced in the dialog. */
  const onBoard = $derived(
    new Set((board?.stages ?? []).flatMap((c) => c.cards).map((c) => c.member_id)),
  );

  /** Split one list into the switcher's boards and the archived ones. Every load goes
   *  through here, and every load asks for the archived rows — otherwise `DELETE` is a
   *  permanent delete wearing a reversible name, because nothing in the UI could ever
   *  name an archived board again. The `enabled` filter on the switcher stops being
   *  vestigial once the list can actually contain a disabled row. */
  function applyList(list: Pipeline[]): void {
    pipelines = list.filter((p) => p.enabled);
    archived = list.filter((p) => !p.enabled);
  }

  async function restoreBoard(key: string): Promise<void> {
    if (skoolId == null) return;
    try {
      const back = await updatePipeline(skoolId, key, { enabled: true });
      applyList(await fetchPipelines(skoolId, true));
      await loadBoard(skoolId, back.key);
      flash(`“${back.name}” is back.`);
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      flash(err instanceof Error ? err.message : 'Could not restore that board.');
    }
  }

  function openCreate(): void {
    formError = null;
    form = { mode: 'create', initial: null };
  }

  function openEdit(): void {
    if (!selected?.manual) return;
    formError = null;
    form = { mode: 'edit', initial: selected };
  }

  async function saveForm(name: string, stages: StageDraft[]): Promise<void> {
    if (skoolId == null || form == null) return;
    formBusy = true;
    formError = null;
    const mode = form.mode;
    const key = form.initial?.key ?? null;
    try {
      const saved = mode === 'create'
        ? await createPipeline(skoolId, name, stages)
        : await updatePipeline(skoolId, key as string, { name, stages });
      form = null;
      // `.filter(enabled)` matches how the page loads this list everywhere else. The
      // server already drops disabled rows without `include_archived`, so this is
      // agreement with the convention rather than a second defence.
      applyList(await fetchPipelines(skoolId, true));
      // Land on what was just authored — creating a board and staying on the previous
      // one makes the button look like it did nothing.
      await loadBoard(skoolId, saved.key);
      flash(mode === 'create' ? `“${saved.name}” created.` : 'Board saved.');
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      formError = err instanceof Error ? err.message : 'That did not save.';
    } finally {
      formBusy = false;
    }
  }

  async function archiveBoard(): Promise<void> {
    if (skoolId == null || !selected?.manual) return;
    const name = selected.name;
    try {
      await archivePipeline(skoolId, selected.key);
      applyList(await fetchPipelines(skoolId, true));
      // `loadBoard` sets `pipelineKey` itself; it only has to be assigned here for the
      // case where nothing is left to select.
      const next = pipelines[0]?.key ?? null;
      if (next) await loadBoard(skoolId, next);
      else {
        pipelineKey = null;
        board = null;
      }
      flash(`“${name}” archived — its cards are kept.`);
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      flash(err instanceof Error ? err.message : 'Could not archive that board.');
    }
  }

  /** Save a card's note. No reason prompt and no optimistic write: the drawer awaits the
   *  saved card so the board adopts the SERVER's copy (which trims, and stamps who and
   *  when) rather than a guess that could disagree with it. */
  async function saveCardNote(cardId: number, note: string | null) {
    if (skoolId == null || pipelineKey == null) throw new Error('no board open');
    const saved = await setCardNote(skoolId, pipelineKey, cardId, note);
    if (board) board = placeCard(removeCard(board, cardId).board, saved);
    return saved;
  }

  function openAdd(stateKey: string): void {
    addError = null;
    adding = stateKey;
  }

  async function addMembers(memberIds: number[], stageKey: string): Promise<void> {
    if (skoolId == null || pipelineKey == null || memberIds.length === 0) return;
    addBusy = true;
    addError = null;
    // SEQUENTIAL, not `Promise.all`. Each add is one INSERT against a unique constraint
    // and the batch is a handful of people; firing them together buys nothing measurable
    // and turns one refusal into an unordered pile of rejected promises.
    const failed: string[] = [];
    let added = 0;
    try {
      for (const id of memberIds) {
        try {
          const card = await addPipelineCard(skoolId, pipelineKey, id, stageKey);
          if (board) board = placeCard(board, card);
          added += 1;
        } catch (err: unknown) {
          if (handle401(err, navigate)) return;
          // PARTIAL SUCCESS IS REPORTED AS PARTIAL. Whoever went on stays on — rolling
          // the batch back because one member was already filed would throw away work
          // that succeeded, to punish a duplicate the board would have shown anyway.
          failed.push(err instanceof Error ? err.message : 'could not be added');
        }
      }
      if (failed.length === 0) {
        adding = null;
        flash(added > 1 ? `Added ${added} to the board.` : 'Added to the board.');
      } else if (added > 0) {
        adding = null;
        flash(`Added ${added}; ${failed.length} could not be added.`);
      } else {
        // Nothing landed, so the dialog stays open with the staged set intact and says
        // why — closing it would discard a selection the reader still wants.
        addError = failed[0] ?? 'Could not add them.';
      }
    } finally {
      addBusy = false;
    }
  }

  async function dropCard(cardId: number): Promise<void> {
    if (skoolId == null || pipelineKey == null || !board) return;
    const { board: without, card } = removeCard(board, cardId);
    board = without; // optimistic: a removal has no server-side placement to disagree with
    try {
      await removePipelineCard(skoolId, pipelineKey, cardId);
      flash('Removed from the board.');
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      if (card && board) board = placeCard(board, card); // put it back
      flash(err instanceof Error ? err.message : 'Could not remove that card.');
    }
  }

  // Page one column further. Columns are unbounded — every member of the community
  // holds a card somewhere — so the board ships a preview and the rest is fetched on
  // demand, per state.
  async function onLoadMore(stateKey: string): Promise<void> {
    if (skoolId == null || pipelineKey == null || !board || loadingMore.has(stateKey)) return;
    const col = board.stages.find((c) => c.key === stateKey);
    if (!col) return;
    loadingMore = new Set(loadingMore).add(stateKey);
    try {
      const { cards, total } = await fetchPipelineStateCards(skoolId, pipelineKey, stateKey, {
        offset: col.cards.length,
        limit: PAGE,
        // `offset` counts rows in the FILTERED, ORDERED result, so page 2 has to be asked
        // under the same filters and the same sort the first page was — otherwise it
        // skips and duplicates rather than merely over-fetching. Hence `appliedFilters`
        // (what the board on screen was built from) and not the live controls: a
        // half-typed search term is not what these offsets belong to.
        ...appliedFilters,
        sort: sortOf(stateKey),
      });
      if (board) {
        // Dedupe on card_id: a card moved by the resolver between the board load and
        // this page would otherwise arrive twice and break the keyed {#each}.
        const seen = new Set(col.cards.map((c) => c.card_id));
        board = {
          ...board,
          stages: board.stages.map((c) =>
            c.key === stateKey
              ? { ...c, total, cards: [...c.cards, ...cards.filter((n) => !seen.has(n.card_id))] }
              : c,
          ),
        };
      }
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      flash('Could not load more cards.');
    } finally {
      const next = new Set(loadingMore);
      next.delete(stateKey);
      loadingMore = next;
    }
  }

  /** Re-read ONE column in a different order, replacing its cards with a fresh first
   *  page. Deliberately not a board reload: the other five columns are answering their
   *  own questions in their own orderings and have no reason to be refetched, and any
   *  extra pages a reader had loaded elsewhere would be thrown away with them. */
  async function onSort(stateKey: string, sort: string): Promise<void> {
    if (skoolId == null || pipelineKey == null || !board) return;
    const col = board.stages.find((c) => c.key === stateKey);
    if (!col || (sorts[stateKey] ?? col.sort) === sort) return;
    sorts = { ...sorts, [stateKey]: sort };
    loadingMore = new Set(loadingMore).add(stateKey);
    try {
      // THE SAME FILTERS THE BOARD IS UNDER. Re-ordering a column narrows nothing: it
      // asks the same filtered set a different question. Sending the sort alone refetched
      // the column unfiltered — the segment's 12 cards came back as all 340, headed by an
      // unfiltered total, while the chip above still claimed a filter was on.
      const { cards, total } = await fetchPipelineStateCards(skoolId, pipelineKey, stateKey, {
        limit: PAGE,
        ...appliedFilters,
        sort,
      });
      if (board) {
        board = {
          ...board,
          // REPLACE, not append: the column is being read in a different order, so the
          // pages already on screen are pages of a different sequence. Keeping them would
          // interleave two orderings into one list that is neither.
          stages: board.stages.map((c) => (c.key === stateKey ? { ...c, total, cards } : c)),
        };
      }
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      flash('Could not re-sort that column.');
    } finally {
      const next = new Set(loadingMore);
      next.delete(stateKey);
      loadingMore = next;
    }
  }

  // --- loading ----------------------------------------------------------------------

  function resetFor(id: number): void {
    skoolId = id;
    pipelines = [];
    pipelineKey = null;
    board = null;
    boardError = null;
    loadingMore = new Set();
    openCardId = null;
    // NO FILTER CLEARING HERE. This runs on the cold load too — inside `withCommunity`,
    // AFTER the filters were read off the URL — so clearing here threw away the `?q=` and
    // `?paying=` of the very link that was opened, and a shared board came up unfiltered.
    // A community SWITCH does need them gone, and says so itself in `reload`.
  }

  async function loadBoard(id: number, key: string): Promise<void> {
    pipelineKey = key;
    boardLoading = true;
    boardError = null;
    openCardId = null;
    // Whatever is in the box at the moment the request goes out is what this board IS,
    // so record it here rather than at keystroke time: `onLoadMore` pages against these
    // offsets and has to use the same term.
    const term = query.trim();
    appliedQuery = term;
    appliedPaying = onlyPaying;
    appliedSegment = segment;
    // A fresh board comes back in each state's DECLARED order, so any override the
    // reader had set no longer describes what is on screen. Dropped rather than
    // re-applied: re-applying would mean N extra requests behind a board load, and the
    // control reads the truth off the column either way.
    sorts = {};
    try {
      // The URL is written from the SAME values the request carries, so a link copied
      // off the address bar rebuilds this exact board rather than a near-miss.
      syncUrl();
      board = await fetchPipelineBoard(id, key, appliedFilters);
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      board = null;
      boardError = err instanceof Error ? err.message : 'Failed to load this pipeline.';
    } finally {
      boardLoading = false;
    }
  }

  function selectPipeline(key: string): void {
    if (skoolId == null || key === pipelineKey) return;
    // The search term SURVIVES a pipeline switch, deliberately: "where is this member"
    // is a question about all of them, and re-typing the name for each board is the
    // whole friction this box exists to remove.
    void loadBoard(skoolId, key);
  }

  /** Re-run the board for the current term, debounced — one request per pause, not per
   *  keystroke, over a board whose columns each run their own filtered query. */
  function onSearchInput(value: string): void {
    query = value;
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      if (query.trim() === appliedQuery) return; // nothing changed, e.g. trailing space
      if (skoolId != null && pipelineKey != null) void loadBoard(skoolId, pipelineKey);
    }, 300);
  }

  /** Toggle the paying-only filter and rebuild the board. No debounce and no early
   *  return: the click IS the intent, and the board has to be refetched either way
   *  because the counts change with it, not only the cards. */
  /** Drop the segment and nothing else. It arrives from a revenue figure rather than
   *  from anything the reader typed, so it needs its own way out — clearing it should
   *  not also throw away a search they are in the middle of. */
  function clearSegment(): void {
    if (!segment) return;
    segment = null;
    if (skoolId != null && pipelineKey != null) void loadBoard(skoolId, pipelineKey);
  }

  function onOnlyPaying(value: boolean): void {
    onlyPaying = value;
    if (skoolId != null && pipelineKey != null) void loadBoard(skoolId, pipelineKey);
  }

  function clearSearch(): void {
    clearTimeout(searchTimer);
    if (!query && !appliedQuery) return;
    query = '';
    if (skoolId != null && pipelineKey != null) void loadBoard(skoolId, pipelineKey);
  }

  /** The account and the pipeline list in the same round trip (doc 04 §4.1); the
   *  board follows once the default pipeline is known. */
  async function init(): Promise<void> {
    if (!getToken()) {
      navigate('/admin/login');
      return;
    }
    loading = true;
    error = null;
    try {
      let fetchedFor: number | null = null;
      const [boot, list] = await Promise.all([
        ensureBootstrap(),
        withCommunity((id) => {
          fetchedFor = id;
          resetFor(id);
          return fetchPipelines(id, true);
        }),
      ]);
      me = asAdminUser(boot);
      if (redirectIfAwaitingOnboarding(me, navigate)) return;
      applyList(list);
      watch.seen(fetchedFor);
      // `?pipeline=` so a board is linkable — the revenue tiles point at the
      // membership one, and "here is the number, here is where you work it" only
      // holds if the link lands on that board rather than on whichever is first.
      // Ignored when it names a board this community doesn't have enabled.
      const wanted = new URLSearchParams(window.location.search).get('pipeline');
      const key = (wanted && pipelines.some((p) => p.key === wanted) ? wanted : null)
        ?? pipelines[0]?.key ?? null;
      if (fetchedFor != null && key) await loadBoard(fetchedFor, key);
      if (fetchedFor != null) void loadRevenue(fetchedFor);
    } catch (err: unknown) {
      watch.rearm();
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load the pipelines board.';
    } finally {
      loading = false;
    }
  }

  // Reload whenever the shared community selection changes (like every admin page).
  // `onCommunityChange` owns the tracking and the dedupe — see its docstring for the
  // superadmin cold-load bug that made this board sit on the wrong community.
  const watch = onCommunityChange((id) => void reload(id));

  async function reload(id: number): Promise<void> {
    dataLoading = true;
    error = null;
    resetFor(id);
    // Filters go with the old community: the same name in another community is a
    // different person, and a segment or a search carried across shows a near-empty board
    // that reads as "this community has no cards".
    resetFilters();
    try {
      const list = await fetchPipelines(id, true);
      applyList(list);
      const key = pipelines[0]?.key ?? null;
      if (key) await loadBoard(id, key);
      void loadRevenue(id);
    } catch (err: unknown) {
      watch.rearm();
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load the pipelines board.';
    } finally {
      dataLoading = false;
    }
  }

  void init();
</script>

<AdminShell {me}>
  {#if loading}
    <StateBlock state="loading" />
  {:else if error && pipelines.length === 0}
    <StateBlock state="error" message={error} onretry={init} />
  {:else}
    <PageContainer>
      <!-- No `updated`: the board is live rows, not a pipeline-computed snapshot, and
           there is no honest instant to stamp it with. -->
      <PageHeader section="Mission Control" title="Pipelines" />
      <!-- Same three tiles as the Overview, from the same block — the board is where
           you act on a slipping payer, so the number belongs beside it. -->
      <!-- DENSE HERE, full on the Overview. Three tiles carrying nine figures, three mix
           bars and three legends pushed the board below the fold on a laptop — and the
           board is what this page is for. Every figure still opens its own segment. -->
      <RevenueTiles
        {revenue}
        updated={revenueAt}
        dense
        onOpenSegment={openSegment}
      />
      {#if dataLoading}
        <StateBlock state="loading" />
      {:else if pipelines.length === 0}
        <div class="rounded-box border hair bg-base-100 px-6 py-14 text-center">
          <div class="mx-auto grid h-11 w-11 place-items-center rounded-full bg-primary/10 text-lg text-primary">⛩</div>
          <h3 class="mt-3 font-display text-lg font-semibold">No pipelines for this community</h3>
          <p class="mx-auto mt-1 max-w-sm text-sm text-base-content/70">
            The built-in boards fill themselves once we have read enough of this
            community. You can also make your own and fill it by hand.
          </p>
          <button class="btn btn-primary btn-sm mt-4" onclick={openCreate}>New pipeline</button>
        </div>
      {:else}
        <!-- Pipeline switcher + board search. The search sits BESIDE the switcher, not
             above a single board, because the term survives switching: find someone
             once, then read across every pipeline to see where they stand. -->
        <div class="mt-5 flex flex-wrap items-center gap-3">
          <div class="flex flex-wrap gap-1.5">
            {#each pipelines as p (p.key)}
              <!-- privacy-ok: a pipeline's name — a board this owner defined, not a person -->
              <button
                class="rounded-field px-3 py-1.5 text-sm font-medium transition-colors {p.key === pipelineKey
                  ? 'bg-primary text-primary-content'
                  : 'bg-base-100 text-base-content/72 ring-1 ring-base-content/10 hover:bg-base-200/60 hover:text-base-content'}"
                aria-pressed={p.key === pipelineKey}
                onclick={() => selectPipeline(p.key)}
              >{p.name}</button>
            {/each}

            <!-- CREATE, AS THE LAST THING IN THE ROW OF BOARDS. It was a primary button in
                 the page header, which put the page's rarest action in its loudest slot
                 and left it competing with the board for the first screen. Here it reads
                 as what it is: after Onboarding, Engagement, Membership and whatever you
                 have made, one more slot — an empty one.

                 GHOSTED, NOT A PILL. The buttons beside it are BOARDS, and one that is
                 not a board must not look like an unselected one. No ring and no fill,
                 muted ink, and it only picks up a background on hover — so it sits at the
                 end of the row without ever competing to be read as a tab.

                 The label animates `max-width` rather than toggling `hidden`, so it stays
                 in the DOM for a screen reader; `focus-visible` opens it too, so tabbing
                 to the control still says what it does. -->
            <button
              class="group flex items-center rounded-field px-2.5 py-1.5 text-sm font-medium text-base-content/40 transition-colors hover:bg-base-200/60 hover:text-base-content/80 focus-visible:bg-base-200/60 focus-visible:text-base-content/80"
              onclick={openCreate}
              aria-label="New pipeline"
              title="New pipeline"
            >
              <span class="text-base leading-none">+</span>
              <span
                class="max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-200
                       group-hover:ml-1.5 group-hover:max-w-[8rem] group-hover:opacity-100
                       group-focus-visible:ml-1.5 group-focus-visible:max-w-[8rem] group-focus-visible:opacity-100"
              >New pipeline</span>
            </button>
          </div>

          <!-- Only for a board this owner authored. A built-in is computed by the tick,
               so there is nothing here to edit and nothing that archiving would not
               simply undo. -->
          <!-- ACTIONS ON THE SELECTED BOARD, not more boards. Unseparated they sat in the
               switcher's own row reading as two further tabs — one of them apparently a
               pipeline called "Archive". The rule divorces them from the pills, and they
               name their subject on hover rather than in a label that would widen the
               row again. -->
          {#if editable}
            <div class="flex items-center gap-0.5 border-l border-base-content/12 pl-2">
              <button class="btn btn-ghost btn-xs font-normal text-base-content/60 hover:text-base-content"
                      title="Rename “{selected?.name}” or change its columns"
                      onclick={openEdit}>Edit</button>
              <button class="btn btn-ghost btn-xs font-normal text-base-content/60 hover:text-error"
                      title="Archive “{selected?.name}” — its cards are kept"
                      onclick={() => void archiveBoard()}>Archive</button>
            </div>
          {/if}

          <!-- The same shape as the other three search boxes in this app
               (<TimelineFilters>, <MembersFilters>, AdminModeration): the BORDER is on
               the label and the input inside it is transparent and outline-less, so the
               icon is a flex sibling rather than something positioned over a control
               that paints its own background. -->
          <!-- THE SEGMENT FILTER, as a control rather than a chip on a row of its own.
               It arrives from a revenue figure, so it is the one filter nobody on this
               page typed — which is exactly why it belongs beside the ones they did,
               wearing the same h-9 pill the "Only paying" toggle wears. It carries its
               own ✕: a filter you cannot see how to remove is a board that looks broken.

               PRIMARY, NOT THE VIOLET. `tint-paying` means "these people pay" — it is
               the same hue the paying cards carry, and that link is the whole reason it
               works. A segment like "at risk to cancel" is not about paying, so it takes
               the app's ordinary accent instead of borrowing a meaning it does not have. -->
          {#if segmentLabel}
            <button
              type="button"
              class="ml-auto flex h-9 shrink-0 items-center gap-2 rounded-xl border border-primary/35 bg-primary/10 px-3 text-sm font-medium text-primary transition-colors hover:bg-primary/[0.16]"
              onclick={clearSegment}
              title="Showing only these members — click to clear"
            >
              {segmentLabel}
              <span class="text-base leading-none text-primary/60">&times;</span>
            </button>
          {/if}

          <!-- ONLY PAYING. Beside the search because it is the same kind of control — a
               board-wide narrowing, counts included — and the two compose: search within
               payers, or read the whole paying roster. Violet when on, matching the tint
               those cards carry, so the filter and its result are the same colour. -->
          <label
            class="flex h-9 shrink-0 cursor-pointer items-center gap-2 rounded-xl border px-3 text-sm transition-colors {segmentLabel ? '' : 'ml-auto'} {onlyPaying
              ? 'tint-paying font-medium'
              : 'border-base-300 bg-base-100 text-base-content/72 hover:bg-base-200/60'}"
            title="Show only members on a paid plan"
          >
            <input
              type="checkbox"
              class="checkbox checkbox-xs"
              checked={onlyPaying}
              onchange={(e) => onOnlyPaying((e.currentTarget as HTMLInputElement).checked)}
            />
            Only paying
          </label>

          <label class="flex h-9 w-full items-center gap-2 rounded-xl border border-base-300 bg-base-100 px-3 focus-within:border-primary sm:w-72">
            <svg class="h-4 w-4 shrink-0 opacity-50" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
            <input
              type="search"
              class="w-full bg-transparent text-sm outline-none placeholder:text-base-content/62"
              placeholder="Find a member…"
              aria-label="Search this board by member name or handle"
              value={query}
              oninput={(e) => onSearchInput((e.currentTarget as HTMLInputElement).value)}
              onkeydown={(e) => e.key === 'Escape' && clearSearch()}
            />
            <!-- No clear button of our own: `type="search"` already draws one, and a
                 second × beside it is what the first version of this shipped with.
                 Clearing natively fires `input` with an empty value, which lands in
                 `onSearchInput` and reloads the unfiltered board like any other edit.
                 The Escape handler stays — Firefox draws no native clear at all. -->
          </label>
        </div>

        <!-- ARCHIVED BOARDS, on their own line under the switcher rather than in it:
             they are not places you can work, so they must not sit among the buttons that
             are. Shown only when some exist — an empty "Archived" label is furniture
             describing nothing. This is what makes archiving reversible in the interface
             and not only in the API. -->
        {#if archived.length > 0}
          <div class="mt-2 flex flex-wrap items-center gap-1.5">
            <span class="text-[0.7rem] text-base-content/45">Archived</span>
            {#each archived as p (p.key)}
              <!-- privacy-ok: a board this owner named, not a person -->
              <button
                class="rounded-field px-2 py-0.5 text-[0.72rem] text-base-content/55 ring-1 ring-base-content/10 transition-colors hover:bg-base-200/60 hover:text-base-content"
                title="Restore “{p.name}” — its cards were kept"
                onclick={() => void restoreBoard(p.key)}
              >{p.name} · restore</button>
            {/each}
          </div>
        {/if}

        <!-- THE CHIP ROW IS GONE, and its own docstring explains why it can be. It
             existed because "a segment arrives in the URL from a revenue tile, so nothing
             the reader typed explains why 87 members became 12, and Only paying is the
             only filter with a visible control". All three now have one: the segment is a
             pill beside Only paying, the search term is in the box it was typed into, and
             each clears itself. A row restating three visible controls is the duplication
             it was built to prevent, one level up. -->

        {#if selected}
          <div class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-base-content/60">
            <span>{pipelineBlurb(selected.kind)}</span>
            {#if board}
              <!-- `board.total` sums the column totals, and those are filtered too, so
                   this reads as the match count without any separate bookkeeping. -->
              <span class="font-mono text-[0.7rem] tabular-nums text-base-content/50">
                {board.total.toLocaleString()}
                {#if appliedQuery}
                  matching “{appliedQuery}”
                {:else}
                  members
                {/if}
                {#if appliedPaying}
                  · paying only
                {/if}
              </span>
            {/if}
          </div>
        {/if}

        <div class="mt-6">
          {#if boardLoading}
            <StateBlock state="loading" />
          {:else if boardError}
            <StateBlock
              state="error"
              message={boardError}
              onretry={() => skoolId != null && pipelineKey != null && loadBoard(skoolId, pipelineKey)}
            />
          {:else if board && appliedQuery && board.total === 0}
            <!-- A searched board with no hits gets its own empty state rather than six
                 empty columns, which read as "this pipeline is broken". -->
            <div class="rounded-box border hair bg-base-100 px-6 py-14 text-center">
              <h3 class="font-display text-lg font-semibold">No member matches “{appliedQuery}”</h3>
              <p class="mx-auto mt-1 max-w-sm text-sm text-base-content/70">
                Search matches a member’s first name, last name or handle in this community.
              </p>
              <button class="btn btn-ghost btn-sm mt-4" onclick={clearSearch}>Clear search</button>
            </div>
          {:else if board}
            <PipelineBoard
              {board}
              {nowMs}
              {loadingMore}
              {sorts}
              onMove={requestMove}
              onUnpin={requestUnpin}
              onAdd={editable ? openAdd : undefined}
              onRemove={editable ? (id) => void dropCard(id) : undefined}
              {onLoadMore}
              onSort={(stateKey, sort) => void onSort(stateKey, sort)}
              onOpen={(id) => (openCardId = id)}
            />
          {/if}
        </div>
      {/if}
    </PageContainer>
  {/if}

  {#if openCard && board && skoolId != null && selected}
    <!-- privacy-ok: a pipeline's name, not a person -->
    <PipelineCardDrawer
      {skoolId}
      pipelineName={selected.name}
      stages={board.pipeline.stages}
      card={openCard}
      {nowMs}
      onMove={requestMove}
      onUnpin={requestUnpin}
      onNote={saveCardNote}
      onClose={() => (openCardId = null)}
    />
  {/if}

  <!-- The "why?" prompt for every manual action. Rendered LAST so it sits above the
       drawer, which is where two of the three actions are raised from. -->
  {#if pending && board}
    {@const stages = board.pipeline.stages}
    {@const stageLabel = (k: string) => stageOf(stages, k)?.label ?? k}
    <CardReasonPrompt
      kind={pending.kind}
      memberName={pendingCard?.member?.name ?? 'this member'}
      fromLabel={pendingCard ? stageLabel(pendingCard.stage_key) : null}
      toLabel={pending.kind === 'unpin' ? null : stageLabel(pending.stageKey)}
      busy={pendingBusy}
      error={pendingError}
      onconfirm={(reason) => void confirmPending(reason)}
      oncancel={cancelPending}
    />
  {/if}

  {#if form}
    <PipelineForm
      mode={form.mode}
      initial={form.initial}
      busy={formBusy}
      error={formError}
      onsave={(name, stages) => void saveForm(name, stages)}
      oncancel={() => (form = null)}
    />
  {/if}

  {#if adding && board}
    <MemberPicker
      skoolId={skoolId ?? 0}
      stages={board.pipeline.stages}
      stageKey={adding}
      alreadyOn={onBoard}
      busy={addBusy}
      error={addError}
      onpick={(memberIds, stageKey) => void addMembers(memberIds, stageKey)}
      oncancel={() => (adding = null)}
    />
  {/if}

  {#if toast}
    <div class="fixed bottom-4 left-1/2 z-[60] -translate-x-1/2 rounded-box border hair bg-base-100 px-4 py-2 text-sm shadow-xl">
      {toast}
    </div>
  {/if}
</AdminShell>
